import React, { useEffect, useRef } from 'react';
import L from 'leaflet';

export interface StopMarkerData {
    id: string;
    name: string;
    lon: number;
    lat: number;
    type: number;
}

interface StopMarkerProps {
    map: ReturnType<typeof L.map> | null;
    stop: StopMarkerData;
    selected?: boolean;
    onClick?: (stopCode: string) => void;
}

// 根据类型获取颜色
const getStopTypeColor = (type: number): string => {
    switch (type) {
        case 1: // 普通停靠点
            return '#1890ff';
        case 2: // 充电桩
            return '#faad14';
        case 3: // 维护点
            return '#722ed1';
        default:
            return '#52c41a';
    }
};

// 根据类型获取图标
const getStopTypeIcon = (type: number): string => {
    switch (type) {
        case 1: // 普通停靠点
            return '<circle cx="12" cy="12" r="4" fill="white"/>';
        case 2: // 充电桩
            return '<path d="M7 2v11h3v9l7-12h-4l4-8H7z" fill="white"/>';
        case 3: // 维护点
            return '<path d="M12 4.5l-9 4.5v7.5l9 4.5 9-4.5V9l-9-4.5zm0 2.5l5.5 2.75L12 12.5 6.5 9.75 12 7z" fill="white"/>';
        default:
            return '<circle cx="12" cy="12" r="4" fill="white"/>';
    }
};

// 创建停靠点图标
const createStopIcon = (type: number, selected: boolean = false): ReturnType<typeof L.divIcon> => {
    const color = getStopTypeColor(type);
    const icon = getStopTypeIcon(type);
    const size = selected ? 28 : 22;

    return (L as any).divIcon({
        className: 'stop-marker',
        html: `
            <div style="
                width: ${size}px;
                height: ${size}px;
                border-radius: 4px;
                background-color: ${color};
                border: 2px solid white;
                box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
            ">
                <svg viewBox="0 0 24 24" width="${size * 0.6}" height="${size * 0.6}">
                    ${icon}
                </svg>
            </div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
};

// 获取停靠点类型名称
const getStopTypeName = (type: number): string => {
    switch (type) {
        case 1:
            return '普通停靠点';
        case 2:
            return '充电桩';
        case 3:
            return '维护点';
        default:
            return '未知类型';
    }
};

/**
 * 停靠点标记组件
 * 在地图上显示停靠点位置
 */
const StopMarker: React.FC<StopMarkerProps> = ({
    map,
    stop,
    selected = false,
    onClick,
}) => {
    const markerRef = useRef<ReturnType<typeof L.marker> | null>(null);

    useEffect(() => {
        if (!map) return;

        const { id, name, lon, lat, type } = stop;

        // 创建或更新标记
        if (!markerRef.current) {
            const marker = (L as any).marker([lat, lon], {
                icon: createStopIcon(type, selected),
            });

            marker.bindPopup(`
                <div style="min-width: 100px;">
                    <div style="font-weight: bold; margin-bottom: 4px;">${name}</div>
                    <div>ID: ${id}</div>
                    <div>类型: ${getStopTypeName(type)}</div>
                </div>
            `);

            marker.on('click', () => {
                onClick?.(id);
            });

            marker.addTo(map);
            markerRef.current = marker;
        } else {
            // 更新位置
            markerRef.current.setLatLng([lat, lon]);
            // 更新图标
            markerRef.current.setIcon(createStopIcon(type, selected));
        }

        return () => {
            if (markerRef.current) {
                markerRef.current.remove();
                markerRef.current = null;
            }
        };
    }, [map, stop, selected, onClick]);

    return null;
};

export default React.memo(StopMarker);
